/**
 * Auth Store (Zustand)
 *
 * Storage strategy:
 * - Access token: localStorage (persists across tab close, screen lock)
 * - User info: localStorage (persists across sessions)
 *
 * Note: Backend uses single JWT (no separate refresh token).
 * On page reload, we call /auth/refresh with the stored token.
 * Token expiry is enforced server-side; logout() clears localStorage.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '../api/types';
import { STORAGE_KEY_AUTH } from '@/lib/constants';
import { getQueryClient } from '@/lib/queryClientRef';
import { useChatInputStore } from './chatInput';
import { useHomeNavStore } from './homeNav';
import { usePendingElicitationsStore } from './pendingElicitations';
import { usePendingTurnsStore } from './pendingTurns';

/**
 * Tear down every MODULE-LEVEL nurse-scoped surface at an identity boundary
 * (logout, or a mid-session swap via setAuth). On a shared bedside device the
 * swap can happen with NO page reload (reconcileIdentity → setAuth), so anything
 * holding the outgoing nurse's data outside a component's lifetime must be purged
 * here or it carries into the next nurse's session. Surfaces:
 *   1. homeNav — the persisted Home selection;
 *   2. the TanStack Query cache — patient list, shift assignments, fetched
 *      session content (PHI);
 *   3. pendingTurns — persisted in-flight chat turn ids (opaque, not PHI, but
 *      must not resume under the next nurse);
 *   4. chatInput — the chat composer draft (a half-typed clinical question);
 *   5. pendingElicitations — the active MCP elicitation's prompt text (clinical).
 *
 * There is deliberately NO persisted response cache anymore: clinical Q&A is
 * never written to localStorage (Joël, 14 October 2026), and the retired key
 * is erased at startup (storage-migration.ts), not here.
 *
 * Intentionally NOT cleared: config / uiPrefs / language (app & device
 * preferences, not nurse data); pendingTurns (only opaque
 * URIs / session-ids / timestamps — non-PHI and self-healing); sseConnection
 * (transport state, reconnects under the new token).
 *
 * NOTE — scope limit: this clears module-level state only. It does NOT reach
 * component-local React state (e.g. a Home rename/share dialog left open, which
 * captures a session title). Those reset when their component unmounts; the
 * residual in-place-swap-with-a-dialog-open case is tracked separately (a full
 * remount-on-identity-change is the fix but is disproportionate to ship here).
 */
function tearDownUserScopedState(): void {
  useHomeNavStore.getState().reset();
  getQueryClient()?.clear();
  usePendingTurnsStore.getState().reset();
  useChatInputStore.getState().setValue('');
  usePendingElicitationsStore.getState().close();
}

interface AuthState {
  accessToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  /** True only after useAuthInit validates the token. Queries must gate on this. */
  authReady: boolean;
  /**
   * Bumped once per identity REPLACEMENT — a swap between two accounts, or a
   * logout that ended someone's session. NOT bumped by a sign-in from anonymous,
   * a same-user token refresh, or a profile edit.
   *
   * It is the view layer's half of the teardown this store already performs:
   * <AuthIdentityBoundary> keys the routed subtree on it, so component-local
   * state (an open dialog holding a session title, a streaming buffer) is
   * discarded on exactly the transitions that carry someone's data. Keeping the
   * decision here rather than re-deriving it in the boundary means the two
   * halves cannot disagree — they did, and the disagreement was the bug: keying
   * on the user id remounted the login pages mid-flight, and re-deriving the
   * rule from a mirrored previous-id made a logout after a sign-in inert.
   *
   * A counter, not a value, because it can never collide with one it already
   * held — the failure mode of keying on the id, where 'anon' meant both "never
   * signed in" and "signed out again".
   */
  identityEpoch: number;

  setAuth: (accessToken: string, user: User) => void;
  setAccessToken: (token: string) => void;
  /** Mark auth as ready for queries. Only useAuthInit should call this. logout() resets it. */
  setAuthReady: () => void;
  updateUser: (updates: Partial<User>) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      user: null,
      isAuthenticated: false,
      authReady: false,
      identityEpoch: 0,

      setAuth: (accessToken, user) => {
        // A shared bedside device can swap the active nurse WITHOUT logout(): a
        // sibling-subdomain login rewrites the domain-wide refresh cookie, and
        // reconcileIdentity's success path resolves that conflict by calling
        // setAuth with the new account directly (see authIdentity.ts). Tear down
        // the previous nurse's scoped state (Home selection + cached PHI) whenever
        // the identity truly changes — same-user re-auth / token refresh
        // (prev.id === user.id) keeps it, which is the whole point of persisting
        // it. This makes setAuth share logout()'s teardown chokepoint, so
        // login/register/verify and every reconcile-success path are covered, not
        // just the failure→logout branch.
        const prev = get().user;
        const identityChanged = prev != null && prev.id !== user.id;
        // Install the new identity FIRST, then tear down — same order as logout()
        // (set → teardown). Teardown's side effects (query refetches after the
        // cache clear) then run under the NEW token, and getState().user never
        // reads as the outgoing nurse mid-teardown.
        set((state) => ({
          accessToken,
          user,
          isAuthenticated: true,
          authReady: true,
          identityEpoch: identityChanged ? state.identityEpoch + 1 : state.identityEpoch,
        }));
        if (identityChanged) {
          tearDownUserScopedState();
        }
      },

      setAccessToken: (accessToken) =>
        set((state) => ({
          accessToken,
          isAuthenticated: state.user !== null,
        })),

      setAuthReady: () => set({ authReady: true }),

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      logout: () => {
        // Bump only when a session actually ended. logout() also runs on paths
        // where nobody was signed in (a failed refresh on a cold load), and an
        // anonymous subtree holds no one's data to discard.
        const endedASession = get().user !== null;
        set((state) => ({
          accessToken: null,
          user: null,
          isAuthenticated: false,
          authReady: false,
          identityEpoch: endedASession ? state.identityEpoch + 1 : state.identityEpoch,
        }));
        // Tear down user-scoped state (Home selection + cached PHI) on every
        // session-END path: explicit logout, 401 token-expiry (client.ts) and the
        // reconcile/refresh FAILURE branches (useAuthInit). Silent token-expiry is
        // the common shared-device session end, so this must clear here too, not
        // just on the "Log out" button. The identity-SWAP paths that don't end the
        // session (reconcile-success onto a different account) tear down in setAuth.
        tearDownUserScopedState();
      },
    }),
    {
      name: STORAGE_KEY_AUTH,
      storage: createJSONStorage(() => localStorage),
      // Persist user and token across tab close, screen lock, browser restart
      partialize: (state) => ({ user: state.user, accessToken: state.accessToken }),
      // Derive isAuthenticated from restored user/token on rehydration
      onRehydrateStorage: () => (state) => {
        if (state && state.user && state.accessToken) {
          state.isAuthenticated = true;
        }
      },
    }
  )
);
